import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useRouter } from 'next/router';
import { MediaUserItem } from '@/types/unFollow';
import { fetchUnfollowData, fetchSearchCheck } from '@/lib/service';
import { getAuth, onAuthStateChanged, User } from 'firebase/auth';
import Cookies from 'js-cookie';
import { useTranslation } from 'next-i18next';
import useMediaNameSync from '@/lib/hooks/useMediaNameSync';
import UnfollowResult from './UnfollowResult';
import UnfollowResultSkeleton from './UnfollowResultSkeleton';
import useLoginAndSubscriptionGuide from '@/lib/hooks/common/useLoginAndSubscriptionGuide';
import UnfollowBanner from './UnfollowBanner';
import Image from 'next/image';
import dynamic from 'next/dynamic';
import useUnFollowPremium from '@/lib/hooks/isPremium/useUnFollowPremium';

const CommonSimpleModal = dynamic(() => import('../Common/CommonSimpleModal'), { ssr: false });
const UpgradeModal = dynamic(() => import('./UpgradeModal'), { ssr: false });
const UserDashboard = dynamic(() => import('../Dashboard/UserDashboard'), { ssr: false });

const analysisSteps = ['Starting', 'Capturing follower list', 'Capturing following list', 'Analysis complete'];

// let searchRecordList: any[] = []

export default function Hero() {
  const resultRef = useRef<HTMLDivElement>(null);
  const { checkLogin } = useLoginAndSubscriptionGuide();
  const [username, setUsername, writeToStorage] = useMediaNameSync('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  // const [showResult, setShowResult] = useState(false);
  // const [titleEnd, setTitleEnd] = useState('You');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const router = useRouter();
  const [result, setResult] = useState<MediaUserItem | null>(null);
  const [loading, setLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const { t } = useTranslation();
  const [openPrivateAccount, setOpenPrivateAccount] = useState<any>(null);
  const { isPremium } = useUnFollowPremium();

  // CLS 优化：showContent 控制骨架屏到内容的同帧切换
  const [showContent, setShowContent] = useState(false);

  // 获取当前用户

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setCurrentUser(user);
      } else {
        setCurrentUser(null);
      }
    });
    return () => unsubscribe();
  }, []);

  // const handleUpgrade = () => {
  //   setShowUpgradeModal(true);
  // };

  const saveCookie = (recordId: number) => {
    const recordListString = Cookies.get('recordListKey1');
    // let searchList = [{ recordId }]

    if (recordListString) {
      try {
        // const recordList = JSON.parse(recordListString);
        // searchList = [...searchList, ...recordList]
        // const searchListString = JSON.stringify(searchList);
        // Cookies.set('recordListKey', searchListString);

        if (recordListString?.length > 0) {
          Cookies.set('recordListKey1', `${recordListString},${recordId}`);
        } else {
          Cookies.set('recordListKey1', `${recordId}`);
        }
      } catch (error) {
        // const recordListStr = JSON.stringify(searchList);
        // Cookies.set('recordListKey', recordListStr);
        Cookies.set('recordListKey1', `${recordId}`);
      }
    } else {
      Cookies.set('recordListKey1', `${recordId}`);
    }
  };

  // TODO: 新增 WhoUnFollow Provider 组件，将相关字段传递给 Pricing 组件，避免通过 URL 传递，影响谷歌统计的准确性
  const handleLoadMore = () => {
    if (!checkLogin()) {
      return;
    }

    if (result?.record_id && !result?.is_private) {
      router.push(
        `/instagram-unfollowers-tracker?record_id=${result?.record_id}&redirect_url=${encodeURIComponent(
          `/dashboard?dashboard_type=toolkit`
        )}#1`,
        undefined,
        {
          shallow: true,
        }
      );
    } else {
      const element = document.getElementById('1');
      if (element) {
        const top = element.getBoundingClientRect().top + window.scrollY - 80;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    }
  };

  useEffect(() => {
    if (isAnalyzing) {
      const interval = setInterval(() => {
        setCurrentStep((prevStep) => {
          if (prevStep === analysisSteps.length - 1) {
            clearInterval(interval);
            setIsAnalyzing(false);
            // setShowResult(true);
            return prevStep;
          }
          return prevStep + 1;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [isAnalyzing]);

  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     setTitleEnd((prev) => (prev === 'You' ? 'Anyone' : 'You'));
  //   }, 3000);
  //   return () => clearInterval(interval);
  // }, []);

  const handleStart = async () => {
    if (!username.trim()) return;

    // CLS 优化：立即设置 loading 状态，让骨架屏和进度条同时显示
    setIsAnalyzing(true);
    setLoading(true);
    setCurrentStep(0);
    setResult(null);
    setShowContent(false);

    // 保持原始行为：进度条出现时滚动到结果区域
    setTimeout(() => {
      if (resultRef?.current) {
        const top = resultRef.current.getBoundingClientRect().top + window.scrollY - 80;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    }, 10);

    const resCheck = await fetchSearchCheck(username);
    if (resCheck?.code === 0 && resCheck.data) {
      // 如果已有数据，重置状态并跳转
      setIsAnalyzing(false);
      setLoading(false);
      router.push(`/dashboard?dashboard_type=toolkit&featureType=unfollower&username=${username.trim()}`);
      return;
    }

    // 手动写入搜索内容到 SessionStorage
    writeToStorage(username);

    const res = await fetchUnfollowData(username, { isPremium, bizFuncName: 'unfollow' });

    if (res?.code === 0) {
      if (res.data?.is_private == true) {
        const data = {
          is_private: 1,
          media_name: res?.data?.media_name,
          full_name: res?.data?.full_name,
          media_avatar: res?.data?.media_avatar,
          profile_pic_url: res?.data?.media_avatar,
        };
        // CLS 优化：出错时保留骨架屏和进度条，等弹窗关闭后再重置
        setOpenPrivateAccount({
          title: t('OopsPrivateProfileDetected'),
          desc: (
            <>
              <div style={{ marginBottom: '24px', textAlign: 'center' }}>{t('ThisInstagramAccountIsPrivate_Desc')}</div>
              <UserDashboard {...data} />
            </>
          ),
          btnTitle: t('TRYAGAIN'),
          btnDesc: (
            <p style={{ color: 'var(--app-text-30-color)', marginTop: '10px' }}>
              {t('Note: Only public accounts can be tracked.')}
            </p>
          ),
        });
        // 不在这里重置 loading 状态，等弹窗关闭后再重置
        return;
      }

      // CLS 优化核心：doShowContent 将所有状态更新集中在一起，同帧切换
      const doShowContent = () => {
        setResult(res?.data);
        setIsAnalyzing(false);
        setLoading(false);
        setShowContent(true);
      };

      // CLS 优化：先滚动到骨架屏位置，滚动完成后再切换内容
      // 这样即使骨架屏和内容高度有差异，CLS 也发生在视口外
      if (resultRef?.current) {
        const top = resultRef.current.getBoundingClientRect().top + window.scrollY - 80;
        window.scrollTo({ top, behavior: 'smooth' });

        // 等待滚动完成后再显示内容
        let scrollTimer: NodeJS.Timeout;
        const handleScrollEnd = () => {
          clearTimeout(scrollTimer);
          scrollTimer = setTimeout(() => {
            window.removeEventListener('scroll', handleScrollEnd);
            doShowContent();
          }, 100);
        };
        window.addEventListener('scroll', handleScrollEnd);
        handleScrollEnd(); // 立即检查一次，以防不需要滚动
      } else {
        doShowContent();
      }

      if (!currentUser && res?.data?.record_id && !res?.data?.is_private) {
        saveCookie(res?.data?.record_id);
      }
    } else {
      // CLS 优化：出错时保留骨架屏和进度条，等弹窗关闭后再重置
      setOpenPrivateAccount({
        title: t('Invalid Instagram Username'),
        desc: t('The username you entered is not valid. Please check the username and try again.'),
        btnTitle: t('TRYAGAIN'),
      });
      // 不在这里重置 loading 状态，等弹窗关闭后再重置
    }
  };

  // const handleCheckMore = () => {
  //   setShowUpgradeModal(true);
  // };

  const getProgressData = useMemo(() => {
    if (!loading) {
      setIsAnalyzing(false);
      return {
        value: `100%`,
        name: analysisSteps[analysisSteps?.length - 1],
      };
    }

    if (loading && currentStep === analysisSteps?.length - 1) {
      return {
        value: `${(currentStep / analysisSteps.length) * 100}%`,
        name: analysisSteps[currentStep - 1],
      };
    }
    return {
      value: `${((currentStep + 1) / analysisSteps.length) * 100}%`,
      name: analysisSteps[currentStep],
    };
  }, [currentStep, loading, isAnalyzing]);

  const loadStatus = useMemo(() => loading || isAnalyzing, [isAnalyzing, loading]);

  return (
    <div className="bg-gradient-to-b  to-gray-50 pt-16 pb-20 px-[16px]">
      <div className="container mx-auto text-center">
        <UnfollowBanner />

        <div className="flex flex-col justify-center items-center gap-4 max-w-md mx-auto" ref={resultRef}>
          {/* <div ref={resultRef} style={{ scrollMarginTop: '70px' }}>
            <p />
          </div> */}

          <input
            type="text"
            placeholder={t('Enter Instagram username')}
            className="w-full px-6 py-3 border-2 bg-white border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition duration-300"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            disabled={isAnalyzing && loading}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleStart();
              }
            }}
          />
          <button
            className={`z-0 w-full px-6 py-3 rounded-lg shadow-md transition duration-300 cursor-pointer ${
              loadStatus
                ? 'bg-pink-300 cursor-not-allowed'
                : 'bg-pink-500 hover:bg-pink-600 hover:shadow-lg text-white transform hover:-translate-y-0.5'
            }`}
            onClick={handleStart}
            disabled={loadStatus}
          >
            {t('Start')}
          </button>

          <p
            className="text-sm text-gray-600 mt-2"
            dangerouslySetInnerHTML={{ __html: t('✨ public account required • no login needs • free trial') }}
          />

          <div className="flex items-center justify-center gap-2 mt-4">
            <div className="flex -space-x-2">
              <Image
                src="https://res-ins.pumpsoul.com/ins_image/1715226267021510004.jpg"
                alt="User avatar"
                className="w-8 h-8 rounded-full border-2 border-white"
                loading="lazy"
                width={32}
                height={32}
              />
              <Image
                src="https://res-ins.pumpsoul.com/ins_image/1715232052557283516.jpg"
                alt="User avatar"
                className="w-8 h-8 rounded-full border-2 border-white"
                loading="lazy"
                width={32}
                height={32}
              />
              <Image
                src="https://res-front.pumpsoul.com/ins-prod/image/picture-43567890.webp"
                alt="User avatar"
                className="w-8 h-8 rounded-full border-2 border-white"
                loading="lazy"
                width={32}
                height={32}
              />
            </div>
            <span className="text-sm text-gray-600">
              <span className="font-semibold">50,000+</span> {t('users trust Dolphin Radar')}
            </span>
          </div>
          {loadStatus && (
            <div className="w-full mt-4">
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className="bg-pink-500 h-2.5 rounded-full transition-all duration-500 ease-out"
                  style={{ width: getProgressData.value }}
                ></div>
              </div>
              <p className="text-sm text-gray-600 mt-2">{getProgressData.name}</p>
            </div>
          )}
        </div>

        {/* CLS 优化：loading 时显示骨架屏预占位，数据返回后同帧切换到真实内容 */}
        {loadStatus && !showContent ? (
          // 搜索中且未显示内容时显示骨架屏
          <UnfollowResultSkeleton />
        ) : showContent && result ? (
          <UnfollowResult result={result} currentUser={currentUser} onLoadMore={handleLoadMore} />
        ) : null}
      </div>

      <UpgradeModal
        isOpen={showUpgradeModal}
        isLoginStatus={!!currentUser}
        onClose={() => setShowUpgradeModal(false)}
        onSignClick={() => {
          setShowUpgradeModal(false);
          // setShowAuthModal(true); //
        }}
        onPricingClick={() => {
          setShowUpgradeModal(false);
          router.push(`/instagram-unfollowers-tracker?media_name=${result?.media_name}#1`);
        }}
      />
      {/*输入错误弹窗 */}
      <CommonSimpleModal
        open={openPrivateAccount !== null}
        title={openPrivateAccount?.title}
        desc={openPrivateAccount?.desc}
        btnTitle={openPrivateAccount?.btnTitle}
        btnDesc={openPrivateAccount?.btnDesc}
        closeAction={() => {
          // CLS 优化：弹窗关闭时再重置 loading 状态，避免骨架屏突然消失造成 CLS
          setOpenPrivateAccount(null);
          setIsAnalyzing(false);
          setLoading(false);
        }}
      />

      {/* ' result?.record_id && !result?.is_private ? `/dashboard/${result?.record_id}` : undefined  //cccc 登陆之后跳啊转操作' */}
    </div>
  );
}
